import { ReactNode, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Button, cn } from "web-design-system";
import {
  AllocationBucket,
  PortfolioReview,
  ReviewExposureSnapshot,
} from "creso-core/types";
import { formatAsPercentage, formatShort } from "creso-core/utils";
import { AmcLogoGroup } from "web-core";
import {
  LegendItem,
  bucketColor,
  mergeBuckets,
} from "../asset-allocation-comparison";
import { AssetAllocationDialog } from "./asset-allocation-dialog";
import { ExposureDialog } from "./exposure-dialog";

const formatPct = (value: string | number) => formatAsPercentage(value, "-", 1);

export function LiveInsights({
  review,
  className,
}: {
  review: PortfolioReview;
  className?: string;
}) {
  const { insights } = review;
  return (
    <aside
      className={cn(
        "flex flex-col gap-4 rounded-lg border border-border bg-card p-3",
        className
      )}
    >
      <div className="flex flex-col gap-3">
        <span className="text-xs font-semibold text-muted-foreground">
          Live insights
        </span>
        <div className="flex gap-6 text-xs text-muted-foreground">
          <LegendItem>Current portfolio</LegendItem>
          <LegendItem dashed>After review</LegendItem>
        </div>
      </div>

      <InsightCard className="flex flex-row gap-3 justify-between">
        <Stat label="Amount moved" value={formatShort(insights.amount_moved)} />
        <Stat label="STCG" value={formatShort(insights.tax.stcg_tax)} />
        <Stat label="LTCG" value={formatShort(insights.tax.ltcg_tax)} />
        <Stat label="Total tax" value={formatShort(insights.tax.total_tax)} />
      </InsightCard>

      <InsightCard
        title="Asset Allocation"
        action={
          insights.asset_allocation.before.length ||
          insights.asset_allocation.after.length ? (
            <DetailsLink
              dialog={AssetAllocationDialog}
              props={insights.asset_allocation}
            />
          ) : null
        }
      >
        <AllocationComparison
          before={insights.asset_allocation.before}
          after={insights.asset_allocation.after}
        />
      </InsightCard>

      <InsightCard
        title="Exposure"
        action={<DetailsLink dialog={ExposureDialog} props={{ review }} />}
      >
        <ExposureComparison
          before={insights.exposure.before}
          after={insights.exposure.after}
        />
      </InsightCard>
    </aside>
  );
}

function InsightCard({
  title,
  action,
  className,
  children,
}: {
  title?: string;
  action?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <section
      className={cn(
        "flex flex-col gap-6 rounded-lg border border-border bg-background p-4",
        className
      )}
    >
      {title ? (
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-semibold">{title}</span>
          {action}
        </div>
      ) : null}
      {children}
    </section>
  );
}

// A "Details" link that opens the card's full view in a dialog.
function DetailsLink<P extends object>({
  dialog: DetailsDialog,
  props,
}: {
  dialog: (
    props: P & { open: boolean; onOpenChange: (open: boolean) => void }
  ) => ReactNode;
  props: P;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        variant="link"
        className="h-auto text-muted-foreground p-0"
        size="sm"
        onClick={() => setOpen(true)}
      >
        Details
      </Button>
      <DetailsDialog {...props} open={open} onOpenChange={setOpen} />
    </>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col items-center gap-2">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm font-semibold">{value}</span>
    </div>
  );
}

// Solid bar for the current portfolio, dashed for after the review.
function InsightBar({
  pct,
  color,
  dashed,
}: {
  pct: number;
  color: string;
  dashed?: boolean;
}) {
  return (
    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
      <div
        className="h-full rounded-full"
        style={{
          width: `${Math.min(Math.max(pct, 0), 100)}%`,
          background: dashed
            ? `repeating-linear-gradient(90deg, ${color} 0 6px, transparent 6px 10px)`
            : color,
        }}
      />
    </div>
  );
}

function BarPair({
  before,
  after,
  color,
}: {
  before: number;
  after: number;
  color: string;
}) {
  return (
    <div className="flex flex-1 flex-col gap-1">
      <div className="flex h-5 items-center gap-4">
        <InsightBar pct={before} color={color} />
        <span className="w-12 text-right text-xs">{formatPct(before)}</span>
      </div>
      <div className="flex items-center h-5 gap-4">
        <InsightBar pct={after} color={color} dashed />
        <span className="w-12 text-right text-xs">{formatPct(after)}</span>
      </div>
    </div>
  );
}

function AllocationComparison({
  before,
  after,
}: {
  before: AllocationBucket[];
  after: AllocationBucket[];
}) {
  const buckets = mergeBuckets(before, after);
  if (!buckets.length) {
    return (
      <span className="text-sm text-muted-foreground">
        Allocation data is not available for these funds.
      </span>
    );
  }
  return (
    <div className="flex flex-col gap-5">
      {buckets.map((bucket, index) => {
        const color = bucketColor(bucket.key, index);
        const subs = mergeBuckets(
          before.find((b) => b.key === bucket.key)?.sub_buckets ?? [],
          after.find((b) => b.key === bucket.key)?.sub_buckets ?? []
        );
        return (
          <div key={bucket.key} className="flex flex-col gap-3">
            <AllocationRow color={color} {...bucket} key={bucket.key} />
            {bucket.key === "indian_equity"
              ? subs.map((sub) => (
                  <AllocationRow
                    key={sub.key}
                    label={sub.label}
                    color={`color-mix(in srgb, ${color} 55%, transparent)`}
                    before={sub.before}
                    after={sub.after}
                    nested
                  />
                ))
              : null}
          </div>
        );
      })}
    </div>
  );
}

function AllocationRow({
  label,
  color,
  before,
  after,
  nested,
}: {
  label: string;
  color: string;
  before: number;
  after: number;
  nested?: boolean;
}) {
  return (
    <div className="flex items-start gap-4">
      <span
        className={cn(
          "w-28 shrink-0 text-sm",
          nested ? "pl-4 text-muted-foreground" : "font-medium"
        )}
      >
        {label}
      </span>
      <BarPair before={before} after={after} color={color} />
    </div>
  );
}

function ExposureComparison({
  before,
  after,
}: {
  before: ReviewExposureSnapshot;
  after: ReviewExposureSnapshot;
}) {
  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-3">
        <ExposureSnapshot snapshot={before} />
        <ArrowRight className="mb-8 size-4 text-muted-foreground" />
        <ExposureSnapshot snapshot={after} highlighted />
      </div>
      <div className="flex flex-col gap-3">
        <span className="text-sm font-semibold text-muted-foreground">
          Largest fund
        </span>
        <LargestFund snapshot={before} />
        <LargestFund snapshot={after} dashed />
      </div>
    </div>
  );
}

function ExposureSnapshot({
  snapshot,
  highlighted,
}: {
  snapshot: ReviewExposureSnapshot;
  highlighted?: boolean;
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-1 items-center">
        <AmcLogoGroup size="xs" items={snapshot.top_amcs} />
        <span className="font-semibold text-sm">
          {snapshot.fund_count} Funds
        </span>
      </div>
      <div
        className={cn(
          "flex flex-col gap-3 rounded-lg p-4",
          highlighted ? "bg-card" : "border border-border"
        )}
      >
        <div className="flex items-center gap-2">
          <span className="w-fit rounded-md border border-border bg-background px-2 py-1 text-xs">
            {snapshot.amc_count} AMCs
          </span>
        </div>
        <span className="text-xs">
          Top 3 AMC: {formatAsPercentage(snapshot.top_3_amc_pct, "-", 0)}
        </span>
      </div>
    </div>
  );
}

function LargestFund({
  snapshot,
  dashed,
}: {
  snapshot: ReviewExposureSnapshot;
  dashed?: boolean;
}) {
  if (!snapshot.largest_fund_name) return null;
  const pct = Number(snapshot.largest_fund_pct);
  return (
    <div className="flex flex-col gap-2">
      <span className="text-xs text-muted-foreground">
        {snapshot.largest_fund_name}
      </span>
      <div className="flex items-center gap-4">
        <InsightBar pct={pct} color="var(--color-chart-1)" dashed={dashed} />
        <span className="w-12 text-right text-xs">{formatPct(pct)}</span>
      </div>
    </div>
  );
}
